import { useState, useEffect, useCallback, useRef } from 'react';
import {
  supportNotificationService,
  SupportNotification,
} from '../services/supportNotificationService';

const SUPPORT_NOTIFICATION_EVENT = 'support-notification-received';

export function dispatchSupportNotification(
  notification: SupportNotification
): void {
  window.dispatchEvent(
    new CustomEvent(SUPPORT_NOTIFICATION_EVENT, { detail: notification })
  );
}

export function useSupportNotifications(inboxId?: string) {
  const [notifications, setNotifications] = useState<SupportNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try
    {
      const data = await supportNotificationService.getNotifications({
        inboxId,
        take: 50,
      });
      if (mountedRef.current)
      {
        setNotifications(data);
        setUnreadCount(data.filter((n) => !n.read).length);
      }
    }
    catch
    {
      // silently fail
    }
    finally
    {
      if (mountedRef.current)
      {
        setLoading(false);
      }
    }
  }, [inboxId]);

  const markAsRead = useCallback(async (id: string) => {
    await supportNotificationService.markAsRead(id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
      )
    );
    setUnreadCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    await supportNotificationService.markAllAsRead(inboxId);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [inboxId]);

  useEffect(() => {
    mountedRef.current = true;
    void fetchNotifications();

    const handleSignalR = (event: Event) => {
      const raw = (event as CustomEvent<SupportNotification & { timestamp?: string }>).detail;
      if (!raw)
      {
        return;
      }

      const notification: SupportNotification = {
        ...raw,
        id: String(raw.id ?? ''),
        inboxId: raw.inboxId ? String(raw.inboxId) : undefined,
        conversationId: raw.conversationId ? String(raw.conversationId) : undefined,
        createdAt: raw.createdAt || raw.timestamp || new Date().toISOString(),
        read: raw.read ?? false,
      };

      if (
        inboxId &&
        notification.inboxId &&
        notification.inboxId.toLowerCase() !== inboxId.toLowerCase()
      )
      {
        return;
      }

      setNotifications((prev) => {
        if (notification.id && prev.some((item) => item.id === notification.id))
        {
          return prev;
        }

        return [notification, ...prev];
      });
      setUnreadCount((c) => c + 1);
    };

    window.addEventListener(SUPPORT_NOTIFICATION_EVENT, handleSignalR);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(SUPPORT_NOTIFICATION_EVENT, handleSignalR);
    };
  }, [fetchNotifications, inboxId]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}

export { SUPPORT_NOTIFICATION_EVENT };

